import { useMemo, useState } from 'react';

import { Bot, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  useAnalystPresets,
  useAnalystReports,
  useCreateAnalystReport,
  useDeleteAnalystReport,
} from '@/hooks/useAnalystReports';
import { useConfiguration, useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useUrlSearchParams } from '@/hooks/useUrlSearchParams';
import { formatDateTime } from '@/utils/format';

import {
  ANALYST_DEFAULT_MODEL,
  ANALYST_DEFAULT_PROVIDER,
  ANALYST_MODELS,
} from '@repo/shared-types';

import { StatusPill } from './StatusPill';

import type { AnalystLlmProvider, AnalystReportRecord } from '@repo/shared-types';

export const AnalystReportsListPage = () => {
  usePageTitle('AI Analyst');
  const { searchParams, updateParams } = useUrlSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const query = useMemo(() => ({ page, limit: 20 }), [page]);

  const { data, isLoading } = useAnalystReports(query);
  const reports = data?.data ?? [];
  const pagination = data?.pagination;

  const { data: presets } = useAnalystPresets();
  const createMutation = useCreateAnalystReport();
  const deleteMutation = useDeleteAnalystReport();
  const { toast } = useToast();

  const [presetKey, setPresetKey] = useState('payment_link_conversion');
  const [provider, setProvider] = useState<AnalystLlmProvider>(ANALYST_DEFAULT_PROVIDER);
  const [model, setModel] = useState<string>(ANALYST_DEFAULT_MODEL[ANALYST_DEFAULT_PROVIDER]);
  const [title, setTitle] = useState('');
  const [configId, setConfigId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AnalystReportRecord | null>(null);

  const selectedPreset = presets?.find((p) => p.key === presetKey);

  const { data: configurations } = useConfigurations();
  // Full config (with versions) only when one is picked.
  const { data: selectedConfig } = useConfiguration(configId);
  const versions = selectedConfig?.versions ?? [];

  const handleGenerate = async () => {
    try {
      await createMutation.mutateAsync({
        preset_key: presetKey as 'payment_link_conversion',
        title: title.trim() || undefined,
        provider,
        model,
        configuration_id: configId || undefined,
        configuration_version_id: versionId || undefined,
      });
      setTitle('');
      toast({
        variant: 'success',
        title: 'Report queued',
        description: 'The AI Analyst is reading the calls. This can take a minute.',
      });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to start report generation.',
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({ variant: 'success', title: 'Report deleted' });
    } catch {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to delete report.' });
    }
  };

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div className="flex items-center gap-3">
        <Bot className="text-primary h-8 w-8" />
        <div>
          <h1 className="text-3xl font-bold">AI Analyst</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Generate root-cause analysis reports from your call data and transcripts.
          </p>
        </div>
      </div>

      {/* Generate panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="text-primary h-5 w-5" />
            New analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Lens</label>
              <select
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                value={presetKey}
                onChange={(e) => setPresetKey(e.target.value)}
              >
                {(presets ?? []).map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">LLM provider</label>
              <select
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                value={provider}
                onChange={(e) => {
                  const next = e.target.value as AnalystLlmProvider;
                  setProvider(next);
                  setModel(ANALYST_DEFAULT_MODEL[next]);
                }}
              >
                <option value="gemini">Google Gemini</option>
                <option value="anthropic">Anthropic (Claude)</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Model</label>
              <select
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {ANALYST_MODELS[provider].map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Configuration (optional)</label>
              <select
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                value={configId}
                onChange={(e) => {
                  setConfigId(e.target.value);
                  setVersionId('');
                }}
              >
                <option value="">All configurations</option>
                {(configurations ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Version (optional)</label>
              <select
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm disabled:opacity-50"
                value={versionId}
                onChange={(e) => setVersionId(e.target.value)}
                disabled={!configId}
              >
                <option value="">Active version</option>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version_number}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Title (optional)</label>
              <input
                className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
                placeholder={selectedPreset?.label ?? 'Report title'}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>

          {selectedPreset && (
            <p className="text-muted-foreground text-sm">{selectedPreset.description}</p>
          )}
          {configId && (
            <p className="text-muted-foreground text-sm">
              The analyst will read this configuration’s prompts and tie failures to specific
              instructions.
            </p>
          )}

          <Button onClick={handleGenerate} disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Generate report
          </Button>
        </CardContent>
      </Card>

      {/* Reports list */}
      <Card>
        <CardHeader>
          <CardTitle>Reports</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground py-8 text-center">Loading…</p>
          ) : reports.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              No reports yet. Generate your first analysis above.
            </p>
          ) : (
            <div className="divide-y">
              {reports.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/ai-analyst/${r.id}`}
                      className="text-primary font-medium hover:underline"
                    >
                      {r.title}
                    </Link>
                    <p className="text-muted-foreground text-xs">{formatDateTime(r.created_at)}</p>
                  </div>
                  <div className="text-muted-foreground hidden w-56 shrink-0 truncate text-xs sm:block">
                    {r.llm_provider ? `${r.llm_provider} · ${r.llm_model ?? '—'}` : '—'}
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusPill status={r.status} />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(r)}
                      aria-label="Delete report"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => updateParams({ page: String(page - 1) })}
              >
                Previous
              </Button>
              <span className="text-muted-foreground text-sm">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.totalPages}
                onClick={() => updateParams({ page: String(page + 1) })}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="report"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
